import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, Clock, ArrowUpRight, ShieldCheck, ShieldAlert } from "lucide-react";
import Layout from "@/components/Layout";
import RemarksModal from "@/components/RemarksModal";
import AmendDecisionModal from "@/components/AmendDecisionModal";
import TransportMonthlySummary from "@/components/TransportMonthlySummary";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import api from "@/lib/api";

const ADMIN_ROLE_LABEL = {
  admin1: "Admin-1 (Bursar)",
  admin1_alt: "Admin-1 Alt (CAO)",
  admin2: "Admin-2 (HOS)",
  super_admin: "Super Admin",
};

function tatChip(r) {
  if (!r.tat_deadline || ["completed", "rejected", "on_hold"].includes(r.status)) return null;
  if (!["hod_review", "admin1_review", "admin2_review"].includes(r.current_stage)) return null;
  const remainingH = (new Date(r.tat_deadline) - new Date()) / 3600000;
  let tone = "bg-emerald-100 text-emerald-800 border-emerald-300";
  let text = `${remainingH.toFixed(1)}h left`;
  if (remainingH <= 0) {
    tone = "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40";
    text = `TAT breached · ${Math.abs(remainingH).toFixed(1)}h ago`;
  } else if (remainingH <= 1) {
    tone = "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40";
  } else if (remainingH <= 4) {
    tone = "bg-amber-100 text-amber-800 border-amber-300";
  }
  return { tone, text };
}

function adminTatLine(deadline) {
  const remainingH = (new Date(deadline) - new Date()) / 3600000;
  const ts = new Date(deadline).toLocaleString();
  const tone = remainingH <= 0 ? "text-[#b3261e]"
             : remainingH <= 4 ? "text-[#b48a45]"
             : "text-[#2d6b6b]";
  const label = remainingH <= 0
    ? `TAT breached ${Math.abs(remainingH).toFixed(1)}h ago`
    : `${remainingH.toFixed(1)} working-hours remaining`;
  return <span className={tone}>TAT deadline: {ts} · {label}</span>;
}

function escalatedChip(r) {
  const stages = r.escalated_stages || [];
  if (!stages.length) return null;
  // Prefer the newest escalation
  const trail = r.audit_trail || [];
  if (stages.includes("admin1")) {
    const evt = trail.slice().reverse().find((a) => a.action === "tat_escalated_admin1_to_admin2");
    // The person BEFORE this escalation was the Admin-1 who owned the stage.
    // Their name is not on the escalation entry itself, but any earlier
    // audit entry with actor_role === 'admin1' will surface a name; fall back
    // to a generic label.
    const prev = trail.slice().reverse().find(
      (a) => (a.actor_role === "admin1" || a.actor_role === "admin1_alt") && a.action !== "tat_escalated_admin1_to_admin2",
    );
    return { from: "Admin-1", who: prev?.actor_name || "Admin-1", at: evt?.at };
  }
  if (stages.includes("hod")) {
    return { from: "HOD", who: r.hod_name || "HOD", at: null };
  }
  return null;
}

function wasEscalatedFrom(r) {
  const c = escalatedChip(r);
  return c ? c.from : null;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isHOS = user?.role === "admin2";
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [actioned, setActioned] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, req: null, action: null });
  const [amend, setAmend] = useState({ open: false, req: null, previousAction: null, previousRemarks: "" });
  const [selected, setSelected] = useState(new Set());
  const [escFilter, setEscFilter] = useState("all"); // all | direct | escalated
  const [filters, setFilters] = useState({ form_type: "", status: "", q: "" });

  const load = () => {
    setLoading(true);
    const superView = isSuperAdmin(user);
    const pendingUrl = superView ? "/requisitions?filter=pending_admin" : "/requisitions?filter=pending_admin_mine";
    Promise.all([
      api.get(pendingUrl),
      api.get("/requisitions?filter=actioned_by_me"),
      api.get("/requisitions?filter=all_admin_level_items"),
      (isHOS || user?.role === "super_admin")
        ? api.get("/analytics/hos-digest") : Promise.resolve({ data: null }),
    ]).then(([p, a, h, d]) => {
      setPending(p.data || []);
      setActioned(a.data || []);
      setHistorical(h.data || []);
      setDigest(d.data);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelected = () => setSelected(new Set());

  const bulkAction = async (action, remarks) => {
    const ids = [...selected];
    const list = pending.filter((r) => selected.has(r.id));
    // client-side guard: warn if different form types selected
    const types = new Set(list.map((r) => r.form_type));
    if (types.size > 1 && action === "approve") {
      const ok = window.confirm(
        `You've selected ${types.size} different form types. Bulk-approve all with the same remark? Reject/hold flows may need individual review.`,
      );
      if (!ok) return;
    }
    let ok = 0, fail = 0;
    for (const r of list) {
      try {
        await api.post(`/requisitions/${r.ref_code}/action`, { action, remarks });
        ok++;
      } catch { fail++; }
    }
    toast[fail === 0 ? "success" : "error"](
      `Bulk ${action}: ${ok} succeeded, ${fail} failed.`, { duration: 5000 },
    );
    clearSelected();
    load();
  };

  const [bulkModal, setBulkModal] = useState({ open: false, action: null });

  const filteredPending = pending.filter((r) => {
    if (escFilter === "direct" && wasEscalatedFrom(r)) return false;
    if (escFilter === "escalated" && !wasEscalatedFrom(r)) return false;
    return true;
  });

  const kpis = useMemo(() => {
    const today = new Date().toDateString();
    const week = Date.now() - 7 * 86400000;
    const approved = actioned.filter(
      (r) => (r.audit_trail || []).some(
        (a) => a.actor_id === user.id && a.action === "approve" &&
               new Date(a.at).toDateString() === today,
      ),
    ).length;
    const onhold = historical.filter((r) => r.status === "on_hold").length;
    const escalated = historical.filter(
      (r) => (r.audit_trail || []).some(
        (a) => a.action?.startsWith("tat_escalated") && new Date(a.at).getTime() > week,
      ),
    ).length;
    return { pending: pending.length, approved, onhold, escalated };
  }, [pending, actioned, historical, user]);

  const filteredHistorical = historical.filter((r) => {
    if (filters.form_type && r.form_type !== filters.form_type) return false;
    if (filters.status && r.status !== filters.status) return false;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      const hay = `${r.ref_code} ${r.applicant_name} ${r.form_data?.mainDept || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const renderCards = (rows, empty, allowBulk = false, forceReadonly = false) => {
    if (loading) return <div className="p-6 text-[#6e7770]">Loading…</div>;
    if (!rows.length) return (
      <div className="sheet p-10 text-center text-[#6e7770]" data-testid="admin-empty">
        <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-[#e8f2f2] border border-[#2d6b6b]/30 flex items-center justify-center">
          <ShieldCheck size={18} className="text-[#2d6b6b]" />
        </div>
        <div className="font-serif text-lg text-[#7a1f2b] mb-1">You have no pending items.</div>
        <div>{empty || "When requisitions arrive, they'll appear here."}</div>
        {isSuperAdmin(user) && (
          <div className="mt-3 text-xs text-[#6e7770]" data-testid="admin-empty-super-hint">
            Super Admin can view all queues by toggling the role tabs above.
          </div>
        )}
      </div>
    );
    return (
      <div className="space-y-3">
        {rows.map((r) => {
          const chip = tatChip(r);
          const esc = escalatedChip(r);
          const superUser = isSuperAdmin(user);
          const canAct = !forceReadonly &&
                         ((r.current_approvers || []).includes(user.id) || superUser) &&
                         !["completed", "rejected"].includes(r.status);
          const isSelected = selected.has(r.id);
          const assignedName = ({
            hod_review: r.hod_name,
            admin1_review: r.admin1_name || (r.admin1_id ? "Admin-1" : "—"),
            admin2_review: r.admin2_name || (r.admin2_id ? "Admin-2 (HOS)" : "—"),
          }[r.current_stage]) || "—";
          const mine = (r.audit_trail || []).slice().reverse().find(
            (a) => a.actor_id === user.id &&
                   ["approve", "on_hold", "reject"].includes(a.action),
          );
          return (
            <div key={r.id} className={`sheet p-5 border-l-4 border-l-[#2d6b6b] hover-lift ${isSelected ? "ring-2 ring-[#2d6b6b]" : ""}`}
                 data-testid={`admin-card-${r.ref_code}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {allowBulk && canAct && (
                    <input type="checkbox" checked={isSelected} onChange={() => toggle(r.id)}
                           className="w-4 h-4 accent-[#2d6b6b]" data-testid={`admin-select-${r.ref_code}`} />
                  )}
                  <Link to={`/requisitions/${r.ref_code}`}
                        className="font-mono font-semibold text-[#2d6b6b] hover:underline"
                        data-testid={`admin-open-${r.ref_code}`}>
                    {r.ref_code}
                  </Link>
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[#e8f2f2] text-[#2d6b6b] text-xs font-medium">
                    {FORM_TYPE_LABEL[r.form_type] || r.form_type}
                  </span>
                  {r.is_emergency && (
                    <span className="badge bg-[#b3261e] text-white border-[#b3261e]" data-testid={`admin-emergency-${r.ref_code}`}>🚨 EMERGENCY</span>
                  )}
                  {r.late_submission && (
                    <span className="badge bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40">LATE</span>
                  )}
                  {esc && (
                    <span className="badge bg-[#f4e9eb] text-[#7a1f2b] border-[#7a1f2b]/40" data-testid={`admin-escalated-${r.ref_code}`}>
                      <ArrowUpRight size={10} className="inline mr-0.5" /> Escalated from {esc.from}: {esc.who}
                    </span>
                  )}
                  {chip && <span className={`badge ${chip.tone}`}>{chip.text}</span>}
                  {superUser && (
                    <span className="badge bg-slate-100 text-slate-700 border-slate-300"
                          data-testid={`admin-assigned-${r.ref_code}`}>
                      Assigned to: {assignedName}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#6e7770] flex items-center gap-1">
                  <Clock size={12} /> {((Date.now() - new Date(r.submitted_at || r.created_at)) / 3600000).toFixed(1)}h ago
                </div>
              </div>
              {r.tat_deadline && (
                <div className="mb-1 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1"
                     data-testid={`admin-tat-line-${r.ref_code}`}>
                  <Clock size={11} /> {adminTatLine(r.tat_deadline)}
                </div>
              )}
              <div className="text-sm text-[#1f2320] mb-1">
                <span className="font-medium">{r.applicant_name}</span> ·
                <span className="text-[#4a524c] ml-1">{r.form_data?.mainDept} / {r.form_data?.subDept}</span>
              </div>
              <div className="text-xs text-[#6e7770] mb-3">
                Stage: <strong>{r.current_stage}</strong>
                {mine && (
                  <span className={`ml-2 badge ${
                    mine.action === "approve" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                    mine.action === "on_hold" ? "bg-amber-100 text-amber-800 border-amber-300" :
                    "bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40"
                  }`} data-testid={`admin-my-action-${r.ref_code}`}>
                    You {mine.action === "on_hold" ? "held" : mine.action + "d"} · {new Date(mine.at).toLocaleDateString()}
                  </span>
                )}
              </div>
              {canAct ? (
                <div className="flex flex-wrap gap-2">
                  <button className="btn-vgws bg-[#2d6b6b] border-[#1c4a4a] hover:bg-[#1c4a4a]"
                          onClick={() => setModal({ open: true, req: r, action: "approve" })}
                          data-testid={`admin-approve-${r.ref_code}`}>
                    <CheckCircle2 size={14} /> Approve
                  </button>
                  <button className="btn-vgws bg-[#b48a45] border-[#8a6a2f] hover:bg-[#8a6a2f]"
                          onClick={() => setModal({ open: true, req: r, action: "on_hold" })}
                          data-testid={`admin-hold-${r.ref_code}`}>
                    <PauseCircle size={14} /> On Hold
                  </button>
                  <button className="btn-vgws btn-danger"
                          onClick={() => setModal({ open: true, req: r, action: "reject" })}
                          data-testid={`admin-reject-${r.ref_code}`}>
                    <XCircle size={14} /> Reject
                  </button>
                  <Link to={`/requisitions/${r.ref_code}`} className="link-vgws ml-auto text-sm inline-flex items-center">
                    Open full detail →
                  </Link>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  {(() => {
                    // Phase 3d — allow amending a prior reject/on_hold decision
                    const lastTerminal = (r.audit_trail || []).slice().reverse().find(
                      (a) => a.action === "reject" || a.action === "on_hold"
                    );
                    const canAmend =
                      ["on_hold", "rejected"].includes(r.current_stage) &&
                      !!lastTerminal &&
                      (superUser || lastTerminal.actor_id === user.id);
                    return canAmend ? (
                      <button
                        onClick={() => setAmend({
                          open: true, req: r,
                          previousAction: lastTerminal.action,
                          previousRemarks: lastTerminal.remarks || "",
                        })}
                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#7a1f2b] text-[#7a1f2b] hover:bg-[#f4e9eb] font-medium"
                        data-testid={`admin-amend-${r.ref_code}`}
                        title="Change your previous reject / on-hold decision"
                      >
                        Change decision
                      </button>
                    ) : <span />;
                  })()}
                  <Link to={`/requisitions/${r.ref_code}`} className="link-vgws text-sm inline-flex items-center">
                    Open full detail →
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Administration</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight" data-testid="admin-heading">
            Administrative Approval Center
          </h1>
          <div className="mt-2 text-sm text-[#4a524c] flex items-center gap-2">
            {isSuperAdmin(user) ? (
              <>
                <ShieldAlert size={14} className="text-[#7a1f2b]" />
                <span className="badge bg-slate-100 text-slate-700 border-slate-300">Super Admin View — all admins</span>
              </>
            ) : (
              <>
                <ShieldCheck size={14} className="text-[#2d6b6b]" />
                <span className="badge bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40">
                  {ADMIN_ROLE_LABEL[user?.role] || user?.role}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 min-w-[420px]">
          <MiniStat label="Pending" value={kpis.pending} tone="teal" testid="admin-stat-pending" />
          <MiniStat label="Approved today" value={kpis.approved} tone="emerald" testid="admin-stat-approved" />
          <MiniStat label="On hold" value={kpis.onhold} tone="gold" testid="admin-stat-onhold" />
          <MiniStat label="Escalations · 7d" value={kpis.escalated} tone="danger" testid="admin-stat-escalations" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4 border-b border-[#e2d7c3] pb-0" data-testid="admin-tabs">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="admin-tab-pending"
                count={pending.length}>Pending My Approval</TabBtn>
        <TabBtn active={tab === "actioned"} onClick={() => setTab("actioned")} testid="admin-tab-actioned"
                count={actioned.length}>Actioned by Me</TabBtn>
        <TabBtn active={tab === "historical"} onClick={() => setTab("historical")} testid="admin-tab-historical"
                count={historical.length}>All Admin-Level Items</TabBtn>
        {(isHOS || user?.role === "super_admin") && (
          <TabBtn active={tab === "digest"} onClick={() => setTab("digest")} testid="admin-tab-digest">HOS Live Digest</TabBtn>
        )}
      </div>

      {/* Phase 5.1 — HOS-only Transportation ROI widget. Role-gated on the
          server; also gated here to keep other admin dashboards clean. */}
      {(isHOS || user?.role === "super_admin") && <TransportMonthlySummary />}

      {tab === "pending" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1" data-testid="admin-esc-filters">
              {[["all", "All"], ["direct", "Direct"], ["escalated", "Auto-escalated"]].map(([k, l]) => (
                <button key={k} onClick={() => setEscFilter(k)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          escFilter === k ? "bg-[#2d6b6b] text-white border-[#1c4a4a]" : "bg-white text-[#4a524c] border-[#e2d7c3] hover:bg-[#e8f2f2]"
                        }`}
                        data-testid={`admin-esc-${k}`}>{l}</button>
              ))}
            </div>
            {selected.size > 0 && (
              <div className="ml-auto sheet px-3 py-2 flex items-center gap-2 border-l-4 border-l-[#2d6b6b] shadow-sm"
                   data-testid="admin-bulk-toolbar">
                <span className="text-sm text-[#1f2320] font-medium">{selected.size} selected</span>
                <button className="btn-vgws bg-[#2d6b6b] border-[#1c4a4a] hover:bg-[#1c4a4a]"
                        onClick={() => setBulkModal({ open: true, action: "approve" })}
                        data-testid="admin-bulk-approve">
                  <CheckCircle2 size={14} /> Approve {selected.size}
                </button>
                <button className="btn-vgws bg-[#b48a45] border-[#8a6a2f] hover:bg-[#8a6a2f]"
                        onClick={() => setBulkModal({ open: true, action: "on_hold" })}
                        data-testid="admin-bulk-hold">
                  <PauseCircle size={14} /> Hold {selected.size}
                </button>
                <button className="btn-vgws btn-danger"
                        onClick={() => setBulkModal({ open: true, action: "reject" })}
                        data-testid="admin-bulk-reject">
                  <XCircle size={14} /> Reject {selected.size}
                </button>
                <button className="link-vgws text-xs ml-1" onClick={clearSelected}>Clear</button>
              </div>
            )}
          </div>
          {renderCards(filteredPending, "No pending approvals right now.", true, false)}
        </>
      )}

      {tab === "actioned" && (
        <>
          <div className="text-sm text-[#6e7770] mb-3">
            Every requisition you have approved, held, or rejected — cards are read-only here.
          </div>
          {renderCards(actioned, "You haven't actioned anything yet.", false, true)}
        </>
      )}

      {tab === "digest" && (
        digest ? (
          <div className="space-y-4">
            {Object.entries(digest).map(([stage, rows]) => (
              rows.length > 0 && (
                <div key={stage} className="sheet p-5 border-l-4 border-l-[#2d6b6b]" data-testid={`digest-${stage}`}>
                  <h3 className="font-serif font-semibold text-[#2d6b6b] mb-2">{stage} ({rows.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[#6e7770] text-xs uppercase tracking-widest">
                          <th className="py-2">Ref</th><th>Form</th><th>Applicant</th>
                          <th>Age (h)</th><th>TAT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.ref_code} className="border-t border-[#f2ecdf]">
                            <td className="py-2 font-mono text-[#2d6b6b] font-semibold">
                              <Link to={`/requisitions/${r.ref_code}`}>{r.ref_code}</Link>
                            </td>
                            <td>{r.form_type}</td>
                            <td>{r.applicant_name}</td>
                            <td>{r.age_hours?.toFixed(1)}</td>
                            <td className={
                              r.tat_state === "breached" ? "text-[#b3261e] font-semibold" :
                              r.tat_state === "at-risk"  ? "text-[#b48a45] font-semibold" :
                              "text-[#2d6b6b]"
                            }>{r.tat_state}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ))}
            {Object.values(digest).every((rows) => !rows.length) && (
              <div className="sheet p-10 text-center text-[#6e7770]" data-testid="digest-empty">
                All clear across the board.
              </div>
            )}
          </div>
        ) : <div className="p-6 text-[#6e7770]">Loading…</div>
      )}

      {tab === "historical" && (
        <>
          <div className="sheet p-4 mb-3 flex flex-wrap gap-2 items-center">
            <select value={filters.form_type} onChange={(e) => setFilters((f) => ({ ...f, form_type: e.target.value }))}
                    className="input-vgws py-2 max-w-[220px]" data-testid="admin-filter-form">
              <option value="">All form types</option>
              {Object.entries(FORM_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="input-vgws py-2 max-w-[200px]" data-testid="admin-filter-status">
              <option value="">Any status</option>
              {["hod_review", "admin1_review", "admin2_review", "process_owner", "accounts", "completed", "rejected", "on_hold"].map((s) =>
                <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Search ref / applicant / dept"
                   value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   className="input-vgws py-2 max-w-[280px]" data-testid="admin-filter-search" />
          </div>
          {renderCards(filteredHistorical, "No requisitions match the filters.", false, true)}
        </>
      )}

      <RemarksModal
        open={modal.open}
        onClose={() => setModal({ open: false, req: null, action: null })}
        onDone={() => { setModal({ open: false, req: null, action: null }); load(); }}
        requisition={modal.req}
        action={modal.action}
        accent="teal"
      />

      <AmendDecisionModal
        open={amend.open}
        requisition={amend.req}
        previousAction={amend.previousAction}
        previousRemarks={amend.previousRemarks}
        onClose={() => setAmend({ open: false, req: null, previousAction: null, previousRemarks: "" })}
        onDone={() => { setAmend({ open: false, req: null, previousAction: null, previousRemarks: "" }); load(); }}
      />

      {/* Bulk modal — inline mini remarks form that calls bulkAction */}
      {bulkModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
             onClick={() => setBulkModal({ open: false, action: null })}
             data-testid="bulk-modal">
          <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#e2d7c3] bg-white">
              <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
                Bulk {bulkModal.action} · {selected.size} requisition{selected.size !== 1 ? "s" : ""}
              </h3>
            </div>
            <BulkRemarksBody
              onCancel={() => setBulkModal({ open: false, action: null })}
              onConfirm={async (remarks) => {
                await bulkAction(bulkModal.action, remarks);
                setBulkModal({ open: false, action: null });
              }}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}

function BulkRemarksBody({ onCancel, onConfirm }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = remarks.trim();
  const minOk = trimmed.length >= 10;
  return (
    <>
      <div className="px-5 py-4 space-y-3">
        <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
          The same remark will be applied to every selected requisition.
        </div>
        <textarea rows={5} autoFocus value={remarks}
                  onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
                  placeholder="Describe the batch decision (min 10 chars)…"
                  className="input-vgws min-h-[120px]" data-testid="bulk-remarks" />
        <div className="text-xs flex items-center justify-between">
          <span className={minOk ? "text-[#2d6b6b]" : "text-[#b3261e]"}>
            {minOk ? "Looks good." : "Minimum 10 characters."}
          </span>
          <span className="font-mono text-[#6e7770]">{trimmed.length}/1000</span>
        </div>
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onCancel} className="btn-ghost" data-testid="bulk-cancel">Cancel</button>
        <button disabled={!minOk || busy}
                onClick={async () => { setBusy(true); try { await onConfirm(trimmed); } finally { setBusy(false); } }}
                className="btn-vgws bg-[#2d6b6b] border-[#1c4a4a] hover:bg-[#1c4a4a]"
                data-testid="bulk-confirm">
          {busy ? "Applying…" : "Confirm to all"}
        </button>
      </div>
    </>
  );
}

function TabBtn({ active, onClick, children, testid, count }) {
  return (
    <button onClick={onClick}
            className={`px-4 py-2 rounded-t-md text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
              active ? "border-[#2d6b6b] text-[#2d6b6b] bg-white" :
                       "border-transparent text-[#4a524c] hover:text-[#2d6b6b]"
            }`}
            data-testid={testid}>
      {children}
      {typeof count === "number" && count > 0 && (
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
          active ? "bg-[#2d6b6b] text-white" : "bg-[#e2d7c3] text-[#4a524c]"
        }`}>{count}</span>
      )}
    </button>
  );
}

function MiniStat({ label, value, tone, testid }) {
  const cls = {
    teal:    "text-[#2d6b6b] border-l-[#2d6b6b]",
    emerald: "text-emerald-800 border-l-emerald-500",
    gold:    "text-[#b48a45] border-l-[#b48a45]",
    danger:  "text-[#b3261e] border-l-[#b3261e]",
  }[tone];
  return (
    <div className={`sheet p-3 border-l-4 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6e7770] font-semibold">{label}</div>
      <div className={`font-serif text-2xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}
